import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { CmsService } from '../cms/cms.service';
import { DOCUMENT } from '@angular/common';
import { FeatureFlagEnvironment } from '@web/csl-lib-models';
import { inject, Injectable, InjectionToken, signal } from '@angular/core';

// Which ButterCMS column (enabled_dev / enabled_test / enabled_prod) applies to this build
export const FEATURE_FLAG_ENVIRONMENT = new InjectionToken<FeatureFlagEnvironment>(
  'FEATURE_FLAG_ENVIRONMENT'
);

export interface FeatureFlag {
  key: string;
  description?: string;
  enabled_dev: boolean;
  enabled_test: boolean;
  enabled_prod: boolean;
}

const FEATURE_FLAGS_COLLECTION = 'feature_flags';
const FEATURE_FLAGS_TIMEOUT_MS = 3000;

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private cms = inject(CmsService);
  private document = inject(DOCUMENT);
  private environment = inject(FEATURE_FLAG_ENVIRONMENT);

  private flags = signal<Record<string, boolean>>({});

  // Fetches the flags once and resolves the ones that apply to the current environment.
  // Never rejects: on failure every flag stays off.
  async load(): Promise<void> {
    const isPreview = new URLSearchParams(this.document.location.search).get('preview') === '1';

    const data = await firstValueFrom(
      this.cms.getCollectionData<{ feature_flags?: FeatureFlag[] }>(FEATURE_FLAGS_COLLECTION, isPreview).pipe(
        timeout(FEATURE_FLAGS_TIMEOUT_MS),
        catchError((error) => {
          console.error('Failed to load feature flags; all flags are off', error);
          return of(null);
        })
      )
    );

    const resolved: Record<string, boolean> = {};
    const items = data?.feature_flags ?? [];

    for (const item of items) {
      resolved[item.key] = item[`enabled_${this.environment}`] === true;
    }

    this.flags.set(resolved);
  }

  // Unknown flags are off. Reads a signal, so it is reactive in templates and computed().
  isEnabled(key: string): boolean {
    return this.flags()[key] === true;
  }
}
